import React, { useState, useEffect, useCallback } from 'react';
import adminService from '../services/adminService';

const ReviewManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReview, setSelectedReview] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const loadReviews = useCallback(async () => {
    setLoading(true);
    try {
      const reviewsData = await adminService.getReviews();
      setReviews(reviewsData);
    } catch (error) {
      console.error('Error loading reviews:', error);
      // Show the specific error message from the service
      showToast(error.message || 'Erro ao carregar avaliações', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  // Moderação removida: não alteramos mais status
  // (removido handleStatusChange por não ser utilizado)

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta avaliação?')) {
      return;
    }
    
    try {
      await adminService.deleteReview(reviewId);
      setReviews(prev => prev.filter(review => review._id !== reviewId));
      showToast('Avaliação excluída com sucesso!', 'success');
    } catch (error) {
      console.error('Error deleting review:', error);
      showToast('Erro ao excluir avaliação', 'error');
    }
  };

  const showToast = (message, type = 'success') => {
    const toast = document.createElement('div');
    const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500';
    const icon = type === 'success' ? 'ph-check-circle' : 'ph-x-circle';
    
    toast.className = `fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 transform translate-x-full transition-transform`;
    toast.innerHTML = `
      <div class="flex items-center">
        <i class="ph ${icon} text-lg mr-2"></i>
        <span>${message}</span>
      </div>
    `;
    
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.classList.remove('translate-x-full');
    }, 100);
    
    setTimeout(() => {
      toast.classList.add('translate-x-full');
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    }, 3000);
  };

  // Derive a safe status from available fields
  // (removido getReviewStatus por não ser utilizado)

  const filteredReviews = reviews.filter(review => {
    // Busca por nome/comentário (sem filtro por status)
    const nameStr = (review?.nome || review?.name || '').toLowerCase();
    const commentStr = (review?.comentario || review?.comment || '').toLowerCase();
    const matchesSearch = nameStr.includes(searchTerm.toLowerCase()) ||
                         commentStr.includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Sempre mostrar como aprovado
  const getStatusColor = () => 'bg-green-100 text-green-800';

  const getStatusText = () => 'Aprovada';

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <i
        key={i}
        className={`ph ${i < rating ? 'ph-star-fill text-yellow-400' : 'ph-star text-gray-300'}`}
      />
    ));
  };

  const getAverageRating = () => {
    // Média considerando todas (já são tratadas como aprovadas)
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + (review?.avaliacao ?? review?.rating ?? 0), 0);
    return (sum / reviews.length).toFixed(1);
  };

  const getReviewStats = () => {
    const total = reviews.length;
    return { total, approved: total, pending: 0, rejected: 0 };
  };

  const stats = getReviewStats();

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Gerenciamento de Avaliações</h2>
            <p className="text-gray-600 mt-1">Gerencie as avaliações dos clientes</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-amber-600">{getAverageRating()}</div>
            <div className="flex items-center justify-end">
              {renderStars(Math.round(getAverageRating()))}
            </div>
            <div className="text-sm text-gray-600 mt-1">Média geral</div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
            <div className="text-sm text-blue-800">Total</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
            <div className="text-sm text-green-800">Aprovadas</div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Buscar por nome ou comentário..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {filteredReviews.map(review => (
          <div key={review._id} className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <h3 className="font-semibold text-gray-900">{review.nome || review.name || 'Sem nome'}</h3>
                    <div className="flex items-center">
                      {renderStars(review.avaliacao ?? review.rating ?? 0)}
                      <span className="ml-2 text-sm text-gray-600">({review.avaliacao ?? review.rating ?? 0}/5)</span>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor()}`}>
                    {getStatusText()}
                  </span>
                </div>
                
                <p className="text-gray-700 mb-3">{review.comentario || review.comment}</p>
                
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>Unidade: {review?.local || review?.unit?.name || review?.unit?.nome || review?.unit || '—'}</span>
                  <span>{new Date(review?.createdAt || review?.data || Date.now()).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex justify-end space-x-2 mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setSelectedReview(review);
                  setShowModal(true);
                }}
                className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title="Ver detalhes"
              >
                <i className="ph ph-eye mr-1"></i>
                Detalhes
              </button>
              
              
              
              <button
                onClick={() => handleDelete(review._id)}
                className="px-3 py-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Excluir"
              >
                <i className="ph ph-trash mr-1"></i>
                Excluir
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredReviews.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <i className="ph ph-chat-circle text-4xl text-gray-400 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm ? 'Nenhuma avaliação encontrada' : 'Nenhuma avaliação ainda'}
          </h3>
          <p className="text-gray-600">
            {searchTerm 
              ? 'Tente ajustar os filtros de busca.'
              : 'As avaliações dos clientes aparecerão aqui.'}
          </p>
        </div>
      )}

      {/* Review Detail Modal */}
      {showModal && selectedReview && (
        <div className="fixed inset-0 z-[100]">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)} />
          {/* Modal Wrapper */}
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Detalhes da Avaliação</h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ph ph-x text-2xl"></i>
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                    <p className="text-gray-900">{selectedReview.nome || selectedReview.name || 'Sem nome'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unidade</label>
                    <p className="text-gray-900">{selectedReview?.local || selectedReview?.unit?.name || selectedReview?.unit?.nome || selectedReview?.unit || '—'}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Avaliação</label>
                  <div className="flex items-center">
                    {renderStars(selectedReview.avaliacao ?? selectedReview.rating ?? 0)}
                    <span className="ml-2 text-gray-600">({selectedReview.avaliacao ?? selectedReview.rating ?? 0}/5)</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Comentário</label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{selectedReview.comentario || selectedReview.comment}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor()}`}>
                      {getStatusText()}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
                    <p className="text-gray-900">{new Date(selectedReview?.createdAt || selectedReview?.data || Date.now()).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
                
                
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewManagement;